import { useThreeContext } from "@/components/common/three-context/three-context";
import {
  flagSubtree,
  isObjLine,
  RenderDispatch,
} from "@/modes/overview-mode/render-dispatch-utils";
import { PointCloudObject } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import { useAutoClipBoxToStore } from "@/tools/use-auto-clip-box";
import {
  CopyToScreenPass,
  EffectPipelineWithSubScenes,
  EyeDomePass,
  FastSSAOPass,
  FilteredRenderPass,
  FxaaPass,
  GapFillPass,
  isExplorationPivot,
  SSAOPass,
  StoreFboPass,
  SubScene,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import { TypedEvent } from "@faro-lotv/foundation";
import { GapFillOptimizedFor, LotvRenderer } from "@faro-lotv/lotv";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, type JSX } from "react";
import { Object3D, Points, PointsMaterial } from "three";
import { selectScanColoring } from "../store/data-preparation-view-options/data-preparation-view-options-selectors";
import { ScanColoring } from "../store/data-preparation-view-options/data-preparation-view-options-slice";
import { usePostProcessingSettings } from "./debug/post-processing-settings";
import { MultiCloudRenderingPolicy } from "./multi-cloud-rendering-policy";

/** Trigger a cheap invalidation of the scene for fast visual feedback. */
export const invalidateSceneCheapEvent = new TypedEvent<void>();
export const invalidateSceneEvent = new TypedEvent<void>();

type RevisionRenderingPipelineProps = {
  /** The point clouds to render in the pipeline. */
  pointCloudObjects: PointCloudObject[];
};

/**
 * @returns A custom rendering pipeline for more efficient point cloud rendering.
 */
export function RevisionRenderingPipeline({
  pointCloudObjects,
}: RevisionRenderingPipelineProps): JSX.Element {
  const renderingPolicy = useMemo(
    () => new MultiCloudRenderingPolicy(pointCloudObjects),
    [pointCloudObjects],
  );

  const storeFboRef = useAutoClipBoxToStore();

  const invalidateSceneCheap = useCallback(
    () => renderingPolicy.invalidateSceneCheap(),
    [renderingPolicy],
  );
  useTypedEvent(invalidateSceneCheapEvent, invalidateSceneCheap);

  const invalidateScene = useCallback(
    () => renderingPolicy.invalidateScene(),
    [renderingPolicy],
  );
  useTypedEvent(invalidateSceneEvent, invalidateScene);

  const scene = useThree((state) => state.scene);
  const size = useThree((state) => state.size);

  // Avoid lag when resizing the browser window, as it often incurs many re-renders in a short time frame
  useEffect(() => invalidateSceneCheap(), [size, invalidateSceneCheap]);

  // Decide which objects to dispatch to which render pass
  useFrame(() => {
    dispatchRenderObjects(scene, pointCloudObjects);
  }, 0);

  return (
    <EffectPipelineWithSubScenes>
      {/* Efficiently render point clouds separately */}
      <SubScene
        filter={(obj: Object3D) =>
          obj.userData.type === RenderDispatch.LodPointCloud
        }
        renderingPolicy={renderingPolicy}
      />

      {/* Capture the point cloud depth buffer for the auto-fitted clipping box. */}
      <StoreFboPass ref={storeFboRef} />

      <PostProcessingEffects />

      {/* Connection lines, to always render them below the scan markers */}
      <FilteredRenderPass
        filter={(obj: Object3D) => obj.userData.type === RenderDispatch.Lines}
        clear={false}
        clearDepth={false}
        transparentBackground
      />

      {/* All other objects */}
      <FilteredRenderPass
        filter={(obj: Object3D) => obj.userData.type === RenderDispatch.Other}
        clear={false}
        clearDepth={false}
        transparentBackground
      />

      {/* Render all transparent objects in this separate pass, but do not clear the buffers.
        This allows the transparency (from anti-aliasing) to be blended correctly.*/}
      <FilteredRenderPass
        filter={(obj: Object3D) =>
          obj.userData.type === RenderDispatch.Pivot && isPivotTextureReady(obj)
        }
        clear={false}
        clearDepth={false}
      />

      <CopyToScreenPass />
    </EffectPipelineWithSubScenes>
  );
}

/**
 * @param obj The 3D object to check
 * @returns true if the pivot object has a loaded texture map, preventing it from
 * rendering as a white square before its SVG texture has finished loading.
 */
function isPivotTextureReady(obj: Object3D): boolean {
  if (obj instanceof Points) {
    const mat = obj.material;
    if (mat instanceof PointsMaterial) {
      return !!mat.map;
    }
  }
  return true;
}

/**
 * Dispatches the objects in the scene to the appropriate rendering steps.
 *
 * @param scene The scene to process
 * @param pointClouds The point clouds in the scene.
 */
function dispatchRenderObjects(
  scene: Object3D,
  pointClouds: PointCloudObject[],
): void {
  const stack = [scene];
  while (stack.length > 0) {
    // Safe because the stack length has been checked
    // Not introducing additional check, because this is in the hot path
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const obj = stack.pop() as Object3D;

    if (Object.values<Object3D>(pointClouds).includes(obj)) {
      flagSubtree(obj, RenderDispatch.LodPointCloud);
    } else {
      if (isExplorationPivot(obj)) {
        obj.userData.type = RenderDispatch.Pivot;
      } else if (isObjLine(obj)) {
        obj.userData.type = RenderDispatch.Lines;
      } else {
        obj.userData.type = RenderDispatch.Other;
      }

      // Recurse into the tree
      stack.push(...obj.children);
    }
  }
}

function PostProcessingEffects(): JSX.Element {
  const { renderer } = useThreeContext();
  const originalColorRendering =
    useAppSelector(selectScanColoring) === ScanColoring.originalColor;
  const {
    isEnabled: isPostProcessingEnabled,
    gapFillSettings,
    ambientOcclusionSettings,
    eyeDomeStrength,
    eyeDomeOutline,
    antiAliasingSettings,
  } = usePostProcessingSettings();
  const supportsHighQualitySSAO =
    renderer instanceof LotvRenderer && renderer.supportsHighQualitySSAO;

  const isEnabled = originalColorRendering && isPostProcessingEnabled;

  return (
    <>
      <GapFillPass
        enabled={isEnabled && gapFillSettings !== undefined}
        optimizedFor={gapFillSettings ?? GapFillOptimizedFor.Speed}
      />
      {supportsHighQualitySSAO ? (
        <SSAOPass
          enabled={isEnabled && ambientOcclusionSettings}
          strength={1.5}
          radius={1.5}
        />
      ) : (
        <FastSSAOPass
          enabled={isEnabled && ambientOcclusionSettings}
          strength={3.0}
          radius={1.5}
        />
      )}
      <EyeDomePass
        enabled={isEnabled && eyeDomeStrength > 0.0}
        showBlackOutlines={eyeDomeOutline}
        strength={eyeDomeStrength}
      />
      <FxaaPass enabled={isEnabled && antiAliasingSettings} />
    </>
  );
}
